import { type CSSProperties, useEffect, useRef, useState } from 'react';
import s from './kiosk-preview.module.css';
import { type IconName, type KioskDoc, PRESETS, accentSoft, t } from './types';

/** Iconițele mockup-ului (aceleași căi ca în aplicația kiosk). */
const ICONS: Record<IconName | 'arrow' | 'contrast' | 'help' | 'globe', string> = {
  locker:
    '<rect x="3" y="2.5" width="18" height="19" rx="3"/><path d="M12 2.5v19M3 9h18M3 15h18"/><circle cx="9" cy="6" r=".9" fill="currentColor" stroke="none"/><circle cx="15" cy="12" r=".9" fill="currentColor" stroke="none"/><circle cx="9" cy="18" r=".9" fill="currentColor" stroke="none"/>',
  box: '<path d="M4 7.5 12 3l8 4.5-8 4.5-8-4.5Z"/><path d="M4 7.5V17l8 4 8-4V7.5M12 12v9"/>',
  pickup:
    '<rect x="4" y="3" width="16" height="18" rx="3"/><path d="M8 8h8M8 12h5M15.5 16.5 17 18l3-3"/>',
  doc: '<path d="M6 2.8h8l4 4V21H6z"/><path d="M14 2.8V7h4M9 11h6M9 15h6"/>',
  gov: '<path d="m3 9 9-5 9 5M5 9h14M6.5 9v8M10.2 9v8M13.8 9v8M17.5 9v8M4 17h16M3 20h18"/>',
  key: '<circle cx="8" cy="12" r="4"/><path d="M12 12h9M17 12v3M20 12v2"/>',
  bed: '<path d="M3 18V9M21 18v-5a3 3 0 0 0-3-3H9a3 3 0 0 0-3 3v5M3 15h18M6 10V7.5A2.5 2.5 0 0 1 8.5 5H11a2 2 0 0 1 2 2v3"/>',
  car: '<path d="m4 14 1.8-5.1A3 3 0 0 1 8.6 7h6.8a3 3 0 0 1 2.8 1.9L20 14"/><rect x="3" y="12" width="18" height="6" rx="2"/><circle cx="7" cy="16" r="1" fill="currentColor" stroke="none"/><circle cx="17" cy="16" r="1" fill="currentColor" stroke="none"/><path d="M5 18v2M19 18v2"/>',
  wrench:
    '<path d="M14.3 6.2a5 5 0 0 0-6.5 6.5L3 17.5 6.5 21l4.8-4.8a5 5 0 0 0 6.5-6.5l-3 3-3.5-1.5-1.5-3.5 4.5-1.5Z"/>',
  cart: '<path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 8H7"/><circle cx="10" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/>',
  support:
    '<path d="M4 13v-2a8 8 0 0 1 16 0v2"/><path d="M4 13h3v6H5a1 1 0 0 1-1-1v-5ZM20 13h-3v6h2a1 1 0 0 0 1-1v-5ZM17 19c0 1.1-1.4 2-3 2h-2"/>',
  shield:
    '<path d="M12 3 20 6v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3Z"/><path d="m9 12 2 2 4-4"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  qr: '<path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-2M14 18h2v2h-2"/>',
  phone:
    '<path d="M7 3H5a2 2 0 0 0-2 2c0 8.8 7.2 16 16 16a2 2 0 0 0 2-2v-2l-4.5-1.5-1.2 2a12 12 0 0 1-8.8-8.8l2-1.2L7 3Z"/>',
  id: '<rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8" cy="11" r="2"/><path d="M5.5 16c.7-1.8 4.3-1.8 5 0M13 10h5M13 14h5"/>',
  check: '<path d="m5 12 4 4L19 6"/>',
  return:
    '<path d="M9 7H5v4"/><path d="M5 11c1.6-3.7 5.7-5.5 9.5-4.2S20.5 12 19 15.5s-5.4 5.2-9 4"/><path d="M9 17H5v4"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 10v6M12 7h.01"/>',
  arrow: '<path d="M5 12h14M14 7l5 5-5 5"/>',
  contrast: '<circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18Z"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.8 9a2.4 2.4 0 1 1 3.8 1.95c-.9.65-1.6 1.1-1.6 2.55M12 17h.01"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18"/>',
};

function Svg({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: ICONS[name] ?? ICONS.info }}
      aria-hidden="true"
    />
  );
}

export function themeVars(doc: KioskDoc): CSSProperties {
  const custom = doc.theme?.custom;
  if (custom && /^#[0-9a-f]{6}$/i.test(custom.accent)) {
    return {
      '--accent': custom.accent,
      '--accent-2': custom.accentDark,
      '--accent-soft': accentSoft(custom.accent),
      '--surface-a': custom.surfaceA,
      '--surface-b': custom.surfaceB,
      '--orb': custom.accent,
      '--orb-2': custom.accentDark,
    } as CSSProperties;
  }
  const p = PRESETS[doc.theme?.preset ?? 'office'] ?? PRESETS.office;
  return {
    '--accent': p.accent,
    '--accent-2': p.accent2,
    '--accent-soft': p.soft,
    '--surface-a': p.surfaceA,
    '--surface-b': p.surfaceB,
    '--orb': p.orb,
    '--orb-2': p.orb2,
  } as CSSProperties;
}

const SYS: Record<string, Record<string, string>> = {
  ro: { available: 'Disponibil', contrast: 'Contrast', help: 'Ajutor' },
  en: { available: 'Available', contrast: 'Contrast', help: 'Help' },
};

/**
 * Ecranul Home al kioskului (D6): același markup și CSS ca mockup-ul, redat la 1080×1920 sau 1280×720 și scalat
 * la lățimea containerului cu `transform: scale`.
 */
export function KioskPreview({
  doc,
  lang,
  orientation = 'portrait',
  className,
}: {
  doc: KioskDoc;
  lang?: string;
  orientation?: 'portrait' | 'landscape';
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    setWidth(el.clientWidth || 360);
    return () => ro.disconnect();
  }, []);
  const W = orientation === 'landscape' ? 1280 : 1080;
  const H = orientation === 'landscape' ? 720 : 1920;
  const scale = width / W;
  const fb = doc.defaultLanguage;
  const l = lang && doc.languages.includes(lang as never) ? lang : fb;
  const sys = SYS[l] ?? SYS.en!;
  return (
    <div
      ref={ref}
      className={[s.frame, className].filter(Boolean).join(' ')}
      style={{ height: H * scale }}
    >
      <main
        className={[s.kiosk, orientation === 'landscape' && s.landscape].filter(Boolean).join(' ')}
        style={{ ...themeVars(doc), width: W, height: H, transform: `scale(${scale})` }}
      >
        <header className={s.header}>
          <div className={s.lockup}>
            <div className={s.mark}>
              <Svg name="locker" />
            </div>
            <div>
              <div className={s.eyebrow}>SYNAPTIC</div>
              <div className={s.product}>{t(doc.header.product, l, fb)}</div>
            </div>
          </div>
          <div className={s.tools}>
            {doc.header.showStatusChip && (
              <div className={s.chip}>
                <span className={s.dot} />
                <span>{sys.available}</span>
              </div>
            )}
            {doc.languages.length > 1 && <div className={s.chip}>{l.toUpperCase()}</div>}
          </div>
        </header>
        <section className={s.main}>
          <div className={s.hero}>
            <div className={s.kicker}>
              <Svg name={doc.hero.icon} />
              {t(doc.hero.kicker, l, fb)}
            </div>
            <h1 className={s.h1}>{t(doc.hero.title, l, fb)}</h1>
            <p className={s.sub}>{t(doc.hero.subtitle, l, fb)}</p>
          </div>
          <div className={s.grid}>
            {doc.actions.map((a) => (
              <div
                key={a.id}
                className={[s.card, a.primary && s.cardPrimary].filter(Boolean).join(' ')}
              >
                <div className={s.actionIcon}>
                  <Svg name={a.icon} />
                </div>
                <div>
                  <h2 className={s.h2}>{t(a.title, l, fb)}</h2>
                  <p className={s.cardText}>{t(a.text, l, fb)}</p>
                </div>
                <div className={s.arrow}>
                  <Svg name="arrow" />
                </div>
              </div>
            ))}
          </div>
          {doc.contextPanel.visible && (
            <div className={s.context}>
              <div className={s.contextIcon}>
                <Svg name={doc.contextPanel.icon} />
              </div>
              <div>
                <strong className={s.contextTitle}>{t(doc.contextPanel.title, l, fb)}</strong>
                <span className={s.contextText}>{t(doc.contextPanel.text, l, fb)}</span>
              </div>
              {t(doc.contextPanel.tag, l, fb) && (
                <div className={s.tag}>{t(doc.contextPanel.tag, l, fb)}</div>
              )}
            </div>
          )}
        </section>
        <footer className={s.footer}>
          <div className={s.note}>{t(doc.footer.note, l, fb)}</div>
          <div className={s.fbtn}>
            <Svg name="contrast" />
            {sys.contrast}
          </div>
          {doc.footer.help.mode !== 'disabled' && (
            <div className={s.fbtn}>
              <Svg name={doc.footer.help.mode === 'phone' ? 'phone' : 'help'} />
              {sys.help}
            </div>
          )}
        </footer>
      </main>
    </div>
  );
}
